import {
    Background,
    Edge,
    MiniMap,
    Node,
    ReactFlow,
    ReactFlowProvider,
    useEdgesState,
    useNodesState,
    NodeMouseHandler,
    NodeChange,
    useReactFlow,
    XYPosition,
} from "@xyflow/react";
import React, {
    memo,
    MouseEvent,
    useCallback, useEffect, useMemo, useState, useRef,
} from "react";
import "@xyflow/react/dist/style.css";
import classNames from "classnames";
import buildFlowFromWorkflows from "./workflow/build-flow";
import {
    edgeTypes, nodeTypes, deleteChildNodes,
    GHOST_NODE_PREFIX,
    injectGhost,
} from "./workflow/nodes";
import { getLayoutedElements, isGroupNode } from "./workflow/flow-layout";
import WorkflowProvider, { useWorkflow } from "./providers/WorkflowProvider";
import WorkflowSidebarProvider, { useWorkflowSidebar } from "./providers/WorkflowSidebarProvider";
import Styles from "./AutomationFlow.module.scss";
import { useTheme } from "../../contexts/Theme";
import { ActionContextType, FormField, Workflow } from "../../__generated__/api";
import ZoomControls from "./components/ZoomControls";
import DragAndDropProvider from "./providers/DragAndDropProvider";
import WorkflowModalProvider from "./providers/WorkflowModalProvider";

function isGroupNodeVisible(node: Node, collapsedGroups: Set<string>, nodesMap: Map<string, Node>) {
    let parentId = (node.data as { parentId?: string })?.parentId;
    while (parentId) {
        if (collapsedGroups.has(parentId)) {
            return false;
        }
        const parentNode = nodesMap.get(parentId);
        parentId = (parentNode?.data as { parentId?: string })?.parentId;
    }
    return true;
}

function AutomationFlow() {
    const theme = useTheme();
    const colorMode = useMemo(() => (theme.getTheme() === "dark" ? "dark" : "light"), [theme]);

    const {
        workflow,
        dirtyTracker,
        temporaryNodeId,
        collapsedGroups,
        setCollapsedGroups,
        ghostParentAndIndex,
        actionsContextType,
    } = useWorkflow();
    const { containerRef } = useWorkflowSidebar();

    const { setCenter, getViewport } = useReactFlow();

    const flow = useMemo(
        () => buildFlowFromWorkflows(workflow?.steps, actionsContextType),
        [workflow?.steps, actionsContextType],
    );
    const [nodes, setNodes, baseOnNodesChange] = useNodesState<Node>(flow.nodes);

    const lastGhostParentIdRef = useRef<string | undefined>();
    const lastGhostInsertIndexRef = useRef<number>(-1);

    // Ignore changes that target the ghost node to prevent redundant state updates
    const onNodesChange = useCallback((changes: NodeChange[]) => {
        const filtered = changes.filter(change => {
            if ("dragging" in change && change.dragging) {
                return false;
            }

            if ("id" in change && typeof change.id === "string" && change.id) {
                return !change.id.startsWith(GHOST_NODE_PREFIX);
            }

            return true;
        });

        if (filtered.length) {
            baseOnNodesChange(filtered);
        }
    }, [baseOnNodesChange]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(flow.edges);

    const handleMiniMapClick = useCallback((_event: MouseEvent, position: XYPosition) => {
        const { zoom } = getViewport();
        setCenter(position.x, position.y, { zoom, duration: 0 });
    }, [getViewport, setCenter]);

    useEffect(() => {
        setNodes(flow.nodes);
        setEdges(flow.edges);
    }, [flow]);

    const [hoveredEdge, setHoveredEdge] = useState<Edge | null>(null);

    const collapsedGroupsSet = useMemo(() => new Set(collapsedGroups), [collapsedGroups]);

    const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
        const { nodes: nodesWithGhost, edges: edgesWithGhost } = ghostParentAndIndex ? injectGhost({
            currentNodes: nodes,
            currentEdges: edges,
            ghostParentId: ghostParentAndIndex.parentId,
            insertIndex: ghostParentAndIndex.index,
            lastGhostParentIdRef,
            lastGhostInsertIndexRef,
        }) : { nodes, edges };

        const nodesMap = new Map(nodesWithGhost.map(nodeItem => [nodeItem.id, nodeItem]));
        const visibleNodes = nodesWithGhost.filter(nodeItem => isGroupNodeVisible(nodeItem, collapsedGroupsSet, nodesMap));

        return getLayoutedElements(visibleNodes, edgesWithGhost);
    }, [nodes, edges, collapsedGroups, ghostParentAndIndex]);

    const handleNodeClick: NodeMouseHandler = useCallback((_event, node) => {
        if (isGroupNode(node)) {
            setCollapsedGroups(prev => {
                const newSet = new Set(prev);
                if (prev.has(node.id)) {
                    newSet.delete(node.id);
                } else {
                    newSet.add(node.id);
                }
                return newSet;
            });
        }
    }, [setCollapsedGroups]);

    const onNodesDelete = useCallback((deleted: Node[]) => {
        const deletedNode = deleted.firstElement;
        const newNodes = deleteChildNodes(deletedNode, nodes, edges);

        setNodes(newNodes);
    }, [nodes, edges]);

    const onEdgeMouseEnter = useCallback((event: MouseEvent, edge: Edge) => {
        // don't show hover for subaction or condition edges and their children
        if (edge.target.includes("urn:fq:workflow-service-action-child")) {
            return;
        }
        setHoveredEdge(edge);
    }, []);

    const onEdgeMouseLeave = useCallback(() => {
        setHoveredEdge(null);
    }, []);

    const edgesWithHovered = useMemo(() => layoutedEdges.map(edgeItem => ({
        ...edgeItem,
        data: {
            ...edgeItem.data,
            hovered: edgeItem.id === (hoveredEdge ? hoveredEdge.id : undefined),
        },
    })), [layoutedEdges, hoveredEdge]);

    return (
        <div className={Styles["react-flow-container"]} ref={containerRef}>
            <ReactFlow
                nodes={layoutedNodes}
                edges={edgesWithHovered}
                nodeTypes={nodeTypes}
                onEdgesChange={onEdgesChange}
                onNodesChange={onNodesChange}
                onNodesDelete={onNodesDelete}
                onNodeClick={handleNodeClick}
                onEdgeMouseEnter={onEdgeMouseEnter}
                onEdgeMouseLeave={onEdgeMouseLeave}
                edgeTypes={edgeTypes}
                fitView
                noDragClassName="nonDraggableNode"
                proOptions={{
                    hideAttribution: true,
                }}
                fitViewOptions={{
                    padding: 1,
                    includeHiddenNodes: false,
                    minZoom: 0.8,
                    maxZoom: 1,
                    nodes: [{ id: temporaryNodeId ?? nodes.firstElement?.id }],
                }}
                colorMode={colorMode}
                panOnScroll
                selectionOnDrag
            >
                <MiniMap
                    position="bottom-left"
                    className={classNames(
                        Styles["mini-map"],
                        dirtyTracker?.isDirty && Styles["mini-map-dirty"],
                    )}
                    pannable
                    onClick={handleMiniMapClick}
                />
                <Background
                    size={2}
                    gap={12}
                    className={Styles["react-flow-background"]}
                    patternClassName={Styles["react-flow-background-pattern"]}
                />
                <ZoomControls />
            </ReactFlow>

            {dirtyTracker?.saveComponent}
        </div>
    );
}

AutomationFlow.defaultProps = {
    onHide: undefined,
    title: undefined,
};

interface AutomationFlowWrapperProps {
    onSaved?: ({ workflowId, shouldReloadForm }: { workflowId?: string; shouldReloadForm?: boolean }) => void;
    workflow?: Workflow;
    onHide?: () => void;
    formFields?: FormField[];
    title?: string;
    actionsContextType?: ActionContextType;
}

export function AutomationFlowWrapper({
    onSaved, workflow, onHide, formFields, title, actionsContextType = ActionContextType.GenericNoTicket,
}: AutomationFlowWrapperProps) {
    return (
        <WorkflowModalProvider title={title} onHide={onHide}>
            <WorkflowProvider
                onSaved={onSaved}
                workflow={workflow}
                formFields={formFields}
                actionsContextType={actionsContextType}
            >
                <ReactFlowProvider>
                    <WorkflowSidebarProvider>
                        <DragAndDropProvider>
                            <AutomationFlow />
                        </DragAndDropProvider>
                    </WorkflowSidebarProvider>
                </ReactFlowProvider>
            </WorkflowProvider>
        </WorkflowModalProvider>
    );
}

export default memo(AutomationFlowWrapper);

AutomationFlowWrapper.defaultProps = {
    onSaved: undefined,
    workflow: undefined,
    onHide: undefined,
    title: undefined,
    formFields: undefined,
    actionsContextType: undefined,
};

export function useAutomationFlow(props: AutomationFlowWrapperProps) {
    const [shouldShow, setShouldShow] = useState(false);

    const handleHide = useCallback(() => {
        setShouldShow(false);
        props.onHide?.();
    }, [props.onHide]);

    const element = useMemo(() => (
        shouldShow ? <AutomationFlowWrapper {...props} onHide={handleHide} /> : null
    ), [props, shouldShow, handleHide]);

    return {
        setShouldShow,
        element,
    };
}
